/**
 * Hand-crafted vector horse — used in-place of bitmap sprites until the
 * Leonardo PNG queue lands. The previous procedural draw was anatomically
 * weak ("rubbish" per user); this rewrite emphasises:
 *
 *   - Recognisable thoroughbred silhouette (3/4 side view, head right).
 *   - Proper head with snout, ears, eye, nostril.
 *   - Visible chest + hindquarters separation, flowing mane + tail.
 *   - Four legs with knee/hock/fetlock joints in distinct mid-stride poses.
 *   - Forward-leaning jockey with helmet, visor, silks pattern (sash + cuffs),
 *     and lane number on the helmet.
 *
 * The horse is drawn ONCE per (frame, silkColor) pair into an offscreen
 * canvas and cached. The runtime path is a single drawImage blit, keeping
 * per-horse render cost well under 0.5 ms even with 8 horses on screen.
 *
 * The body part draw routines live in `horse-procedural-parts.ts` to keep
 * this file focused on the public draw API + cache plumbing.
 */

import type { TrackHorse } from '../state/types.js';
import { renderHorseInto } from './horse-procedural-parts.js';

// Public draw size (kept consistent with track-draw lane geometry).
export const HORSE_LEN = 116;
export const HORSE_THICK = 50;

// Offscreen canvas dims include slack for the jockey above the body and the
// extended legs below it.
const SPRITE_W = HORSE_LEN + 36;
const SPRITE_H = HORSE_THICK + 96;

interface CachedSprite { canvas: HTMLCanvasElement; ox: number; oy: number; }

// Cache key: `${frame}:${silkColor}:${number}` -> pre-rendered tinted sprite.
const _cache = new Map<string, CachedSprite>();

/**
 * Render the high-detail horse at the current ctx transform. Internally
 * blits a cached offscreen sprite for the (frame, silkColor) pair; the first
 * call for a new key pays the full vector draw cost (~1ms), subsequent calls
 * are sub-ms drawImage blits.
 */
export function drawProceduralHorse(ctx: CanvasRenderingContext2D, h: TrackHorse, frame: number): void {
  const sprite = getOrRenderSprite(frame, h.silkColor, h.number);
  ctx.drawImage(sprite.canvas, -sprite.ox, -sprite.oy);
}

function getOrRenderSprite(frame: number, silkColor: string, number: number): CachedSprite {
  const key = `${frame}:${silkColor}:${number}`;
  const cached = _cache.get(key);
  if (cached) return cached;
  const canvas = document.createElement('canvas');
  canvas.width = SPRITE_W;
  canvas.height = SPRITE_H;
  const c = canvas.getContext('2d');
  if (!c) {
    const empty: CachedSprite = { canvas, ox: 0, oy: 0 };
    _cache.set(key, empty);
    return empty;
  }
  // Origin offset chosen so the horse "floats" centred at SPRITE_W/2,
  // SPRITE_H/2 + 12 (legs hang below body centre).
  const ox = SPRITE_W / 2;
  const oy = SPRITE_H / 2 + 12;
  c.translate(ox, oy);
  c.imageSmoothingEnabled = true;
  c.imageSmoothingQuality = 'high';
  renderHorseInto(c, { frame, silkColor, number, len: HORSE_LEN, thick: HORSE_THICK });
  const sprite: CachedSprite = { canvas, ox, oy };
  _cache.set(key, sprite);
  return sprite;
}
